// HubPilot runtime: added to the top of every robot setup sent to the hub. Lazy-connects the Xbox controller,
// stops every motor if the controller drops out, then reconnects automatically.
export const PRELUDE = `# Made with HubPilot.au - robot driving setup for LEGO SPIKE Prime + Xbox controllers
# Runs on the official Pybricks firmware (pybricks.com)
from pybricks.hubs import PrimeHub
from pybricks.pupdevices import Motor
from pybricks.parameters import Port, Direction, Button, Color, Icon
from pybricks.robotics import DriveBase
from pybricks.tools import wait, StopWatch

try:
    from pybricks.iodevices import XboxController
except ImportError:
    XboxController = None

try:
    from urandom import randint
except ImportError:
    try:
        from random import randint
    except ImportError:
        def randint(a, b):
            return a + hs_timer.time() % (b - a + 1)

hub = PrimeHub()
hs_timer = StopWatch()
hs_motors = {}
hs_dirs = {}
hs_cfg = [Port.A, Port.B, 56, 112]
hs_state = {"base": None, "xbox": None, "base_active": False}
hs_rc = {"left": [], "right": [], "omni": [], "k": 1, "steer": None, "steer_max": 45, "drive": []}
hs_att = {}


def hs_clamp(value, low=-100, high=100):
    return max(low, min(high, value))


def hs_speed(percent):
    return hs_clamp(percent) * 10


def hs_motor(port):
    m = hs_motors.get(port)
    if m is None:
        m = Motor(port, hs_dirs.get(port, Direction.CLOCKWISE))
        hs_motors[port] = m
    return m


def hs_motor_dir(port, direction):
    hs_dirs[port] = direction
    hs_motors.pop(port, None)
    hs_state["base"] = None


def hs_drive_setup(left, right, wheel, axle):
    hs_cfg[0] = left
    hs_cfg[1] = right
    hs_cfg[2] = wheel
    hs_cfg[3] = axle
    if left not in hs_dirs:
        hs_motor_dir(left, Direction.COUNTERCLOCKWISE)
    hs_state["base"] = None


def hs_db():
    if hs_state["base"] is None:
        hs_state["base"] = DriveBase(hs_motor(hs_cfg[0]), hs_motor(hs_cfg[1]), hs_cfg[2], hs_cfg[3])
    hs_state["base_active"] = True
    return hs_state["base"]


def hs_tank(left, right):
    if hs_rc["left"] or hs_rc["right"]:
        for m in hs_rc["left"]:
            m.dc(hs_clamp(left))
        for m in hs_rc["right"]:
            m.dc(hs_clamp(right))
        return
    if hs_state["base_active"]:
        hs_state["base"].stop()
        hs_state["base_active"] = False
    hs_motor(hs_cfg[0]).dc(hs_clamp(left))
    hs_motor(hs_cfg[1]).dc(hs_clamp(right))


def hs_arcade(forward, turn):
    hs_tank(forward + turn, forward - turn)


def hs_drive_stop():
    if hs_state["base"] is not None:
        hs_state["base"].stop()
        hs_state["base_active"] = False
    for port in (hs_cfg[0], hs_cfg[1]):
        if port in hs_motors:
            hs_motors[port].brake()
    for m in hs_rc["left"] + hs_rc["right"] + hs_rc["drive"] + [w[0] for w in hs_rc["omni"]]:
        m.brake()


def hs_rc_motor(spec):
    port, direction = spec
    hs_motor_dir(port, direction)
    return hs_motor(port)


def hs_tank_setup(left, right):
    hs_rc["left"] = [hs_rc_motor(s) for s in left]
    hs_rc["right"] = [hs_rc_motor(s) for s in right]


def hs_omni_setup(wheels, k):
    # Each wheel: (port, direction, x push, forward push) when its motor spins forwards.
    hs_rc["omni"] = [(hs_rc_motor((p, d)), hx, hy) for p, d, hx, hy in wheels]
    hs_rc["k"] = k


def hs_omni(forward, sideways, turn):
    if not hs_rc["omni"]:
        raise RuntimeError("The drivetrain isn't set up. Open Edit setup and save it again")
    powers = [(hx * sideways + hy * forward) * hs_rc["k"] + turn for _, hx, hy in hs_rc["omni"]]
    top = max([100] + [abs(p) for p in powers])
    for i in range(len(powers)):
        hs_rc["omni"][i][0].dc(powers[i] * 100 / top)


def hs_car_setup(steer, max_angle, drives):
    m = hs_rc_motor(steer)
    m.reset_angle(0)
    hs_rc["steer"] = m
    hs_rc["steer_max"] = max_angle
    hs_rc["drive"] = [hs_rc_motor(s) for s in drives]


def hs_car(speed, steer):
    if hs_rc["steer"] is None:
        raise RuntimeError("The steering isn't set up. Open Edit setup and save it again")
    hs_rc["steer"].track_target(hs_clamp(steer) * hs_rc["steer_max"] / 100)
    for m in hs_rc["drive"]:
        m.dc(hs_clamp(speed))


def hs_stop_all():
    if hs_state["base"] is not None:
        try:
            hs_state["base"].stop()
        except OSError:
            pass
    for m in hs_motors.values():
        try:
            m.stop()
        except OSError:
            pass
    for att in hs_att.values():
        att[1] = True


def hs_xbox():
    if XboxController is None:
        raise RuntimeError("No Xbox support in this firmware. Install the latest Pybricks firmware.")
    while hs_state["xbox"] is None:
        hub.light.blink(Color.ORANGE, [150, 150])
        print("HubPilot: searching for Xbox controller...")
        try:
            hs_state["xbox"] = XboxController()
            hub.light.on(Color.GREEN)
            print("HubPilot: controller connected")
        except OSError:
            wait(250)
    return hs_state["xbox"]


def hs_lost():
    old = hs_state["xbox"]
    hs_state["xbox"] = None
    hs_stop_all()
    print("HubPilot: controller lost - motors stopped, reconnecting")
    try:
        old.disconnect()
    except Exception:
        pass


def hs_stick(side, axis):
    try:
        c = hs_xbox()
        return (c.joystick_right() if side else c.joystick_left())[axis]
    except OSError:
        hs_lost()
        return 0


def hs_trigger(side):
    try:
        return hs_xbox().triggers()[side]
    except OSError:
        hs_lost()
        return 0


def hs_button(button):
    try:
        return button in hs_xbox().buttons.pressed()
    except OSError:
        hs_lost()
        return False


def hs_press(b):
    # How hard an attachment button is pressed, 0 to 100 (triggers are gradual).
    if b == "LT":
        return hs_trigger(0)
    if b == "RT":
        return hs_trigger(1)
    return 100 if hs_button(b) else 0


def hs_attach(specs, pos, neg, speed, hold):
    key = specs[0][0]
    att = hs_att.get(key)
    if att is None:
        att = [[hs_rc_motor(s) for s in specs], True]
        hs_att[key] = att
    power = (hs_press(pos) - hs_press(neg)) * hs_clamp(speed, 0, 100) / 100
    if abs(power) > 3:
        for m in att[0]:
            m.dc(power)
        att[1] = True
    elif att[1]:
        for m in att[0]:
            if hold:
                m.hold()
            else:
                m.stop()
        att[1] = False


def hs_rumble(power, seconds):
    try:
        hs_xbox().rumble(hs_clamp(power, 0, 100), int(seconds * 1000), 1, 0)
    except OSError:
        hs_lost()


def hs_connected():
    return hs_state["xbox"] is not None
`;

export const IDENTIFY_PROGRAM = `from pybricks.hubs import PrimeHub
from pybricks.parameters import Color, Icon
from pybricks.tools import wait

hub = PrimeHub()
print("HubPilot: this is me!")
hub.display.icon(Icon.HEART)
for i in range(5):
    hub.light.on(Color.ORANGE)
    hub.speaker.beep(880, 120)
    wait(200)
    hub.light.on(Color.BLUE)
    wait(200)
hub.display.off()
`;
